<script setup lang="ts">
  import type { EmblaCarouselType } from 'embla-carousel';

  defineEmits<{ click: [] }>();

  const { activeBreakpoint, windowWidth } = useDeviceType();
  const isMobile = computed(() => ['xxs', 'xs', 'sm'].includes(activeBreakpoint.value));
  const isTablet = computed(() => ['md', 'lg'].includes(activeBreakpoint.value));

  const gameList = computed(() => {
    const list = [
      {
        name: '21 Rush',
        imagePoster: '/_ipx/_/images/lo-hp-new/card-cover-21-rush.webp',
        imageCover: '/images/lo-hp-new/card-cover-21-rush.webp',
        imageLogo: '/images/lo-hp-new/card-logo-21-rush.webp',
        videoSrc: '/videos/lo-new/Cards_21Rush.mp4',
        description:
          'Reach 21 to clear a stack without busting. Earn bonus rewards with 5 card stacks or consecutive clears',
        width: 200,
        height: 192,
      },
      {
        name: 'Olympus Plinko',
        imagePoster: '/_ipx/_/images/lo-hp-new/card-cover-olympus-plinko.webp',
        imageCover: '/images/lo-hp-new/card-cover-olympus-plinko.webp',
        imageLogo: '/images/lo-hp-new/card-logo-olympus-plinko.webp',
        videoSrc: '/videos/lo-new/Cards_OlympusPlinko.mp4',
        description:
          'Play reimagined and unique Plinko games - earn up to 9x in Player vs Player or hit the Jackpots in FastPlay for epic wins',
        width: 154,
        height: 66,
      },
      {
        name: 'Scarab Plinko',
        imagePoster: '/_ipx/_/images/lo-hp-new/card-cover-scarab-plinko.webp',
        imageCover: '/images/lo-hp-new/card-cover-scarab-plinko.webp',
        imageLogo: '/images/lo-hp-new/card-logo-scarab-plinko-sp.webp',
        videoSrc: '/videos/lo-new/Cards_ScarabPlinko.mp4',
        description:
          'Hit scarabs to apply multipliers to your score. Use the bump to the left or right to control ball direction and improve your score.',
        width: 123,
        height: 112,
      },
    ];

    if (isMobile.value || isTablet.value) return list;
    return [...list, ...list];
  });

  const carousel = useTemplateRef('carousel');
  const api = shallowRef<EmblaCarouselType | undefined>(undefined);

  const activeSlideIndex = ref(0);
  const transitionActive = ref(false);
  const videoElements = shallowRef<HTMLVideoElement[]>([]);

  const slidesGap = computed(() => {
    if (!windowWidth.value || windowWidth.value < 1660) return 'gap-auto';
    return 'gap-[5rem]';
  });

  const onSlideScrolling = useThrottleFn(() => {
    transitionActive.value = true;
  }, 200);

  function onSlideSettled() {
    transitionActive.value = false;
  }

  function onSlideChanged(instance: EmblaCarouselType) {
    const index = instance.internalEngine()?.index?.get();
    activeSlideIndex.value = index;
    if (!isMobile.value && !isTablet.value) return;
    videoElements.value.forEach((el: HTMLVideoElement, i) => el[i === index ? 'play' : 'pause']?.());
  }

  onMounted(() => {
    api.value = carousel.value?.emblaApi;

    api.value?.on('slidesInView', onSlideChanged);
    api.value?.on('scroll', onSlideScrolling);
    api.value?.on('settle', onSlideSettled);
  });

  onBeforeUnmount(() => {
    api.value?.off('slidesInView', onSlideChanged);
    api.value?.off('scroll', onSlideScrolling);
    api.value?.off('settle', onSlideSettled);
  });
</script>

<template>
  <template v-if="isMobile">
    <p
      class="px-6 text-white text-center font-barlow text-base not-italic font-normal leading-[135%] transition-opacity ease-in-out duration-200"
      :class="{ 'opacity-0': transitionActive }"
    >
      {{ gameList[activeSlideIndex]?.description }}
    </p>

    <div class="w-screen -mx-4 md:mx-auto min-h-[23.75rem]">
      <UCarousel
        ref="carousel"
        v-slot="{ item, index }"
        class="carousel"
        :items="gameList"
        align="center"
        loop
        :ui="{
          viewport: 'h-full',
          container: 'h-full',
          item: `self-stretch aspect-[13/20] basis-2/3 relative after:content-[''] after:block after:absolute after:bottom-0 after:w-full after:h-[100px] after:bg-gradient-to-t after:from-[#161d26] after:via-[#161d26]/[0.85] after:to-transparent`,
        }"
      >
        <div class="relative" @click="$emit('click')">
          <video
            :id="`video-item-${index}`"
            :ref="(el) => (videoElements[index] = el as HTMLVideoElement)"
            loop
            autoplay
            muted
            playsinline
            webkit-playsinline
            width="100%"
            preload="auto"
            :poster="item.imagePoster"
            class="rounded-10px overflow-hidden"
          >
            <source :src="item.videoSrc" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <NuxtImg :src="item.imageCover" class="hidden opacity-0" />
          <NuxtImg
            class="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-4 max-w-[75%] mx-auto z-10"
            :src="item.imageLogo"
            densities="x1 x2"
            format="webp"
            :alt="`${item.name} logo`"
            :width="item.width"
            :height="item.height"
          />
        </div>
      </UCarousel>
    </div>

    <div class="w-full px-6">
      <CoreButton color="green" class="w-full h-12 px-8 py-4 justify-center items-center" @click="$emit('click')">
        <span class="font-inter text-[0.875rem] font-bold leading-none">PLAY NOW</span>
      </CoreButton>
    </div>
  </template>
  <template v-else-if="isTablet">
    <div class="w-full min-h-[23.75rem]">
      <UCarousel
        ref="carousel"
        v-slot="{ item, index }"
        class="carousel"
        :items="gameList"
        align="start"
        loop
        arrows
        :ui="{
          viewport: 'h-full',
          container: 'h-full',
          item: 'self-stretch aspect-[3/2] basis-5/6 min-h-[25.8125rem]',
        }"
        :prev="{
          variant: 'solid',
          ui: {
            base: [
              'flex justify-center items-center gap-2.5 shrink-0',
              'border! border-white/10! bg-white/5! rounded-10px! w-[58px]! h-[58px]! translate-x-6 p-0',
            ],
          },
        }"
        :next="{
          variant: 'solid',
          ui: {
            base: [
              'flex justify-center items-center gap-2.5 shrink-0',
              'border! border-white/10! bg-white/5! rounded-10px! w-[58px]! h-[58px]! -translate-x-6 p-0',
            ],
          },
        }"
        prev-icon="i-lucide-chevron-left"
        next-icon="i-lucide-chevron-right"
      >
        <div
          class="flex py-1.5 pr-[2.875rem] pl-1.5 h-full items-center gap-10 rounded-[10px] border border-white/10 bg-white/5"
          @click="$emit('click')"
        >
          <video
            :id="`video-item-${index}`"
            :ref="(el) => (videoElements[index] = el as HTMLVideoElement)"
            loop
            autoplay
            muted
            playsinline
            webkit-playsinline
            width="100%"
            preload="auto"
            :poster="item.imagePoster"
            class="rounded-10px overflow-hidden aspect-[245/380] h-full object-cover"
          >
            <source :src="item.videoSrc" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <NuxtImg :src="item.imageCover" class="hidden opacity-0" />
          <div class="h-full w-full py-0 flex flex-col items-start justify-evenly">
            <NuxtImg
              class="max-w-[90%] max-h-[8.75rem]"
              :src="item.imageLogo"
              densities="x1 x2"
              format="webp"
              :alt="`${item.name} logo`"
            />

            <div class="flex flex-col gap-6">
              <p class="text-left text-white/80 text-xl leading-[135%]">{{ item.description }}</p>

              <CoreButton
                color="green"
                class="w-full h-[3.125rem] px-8 py-4 justify-center items-center"
                @click="$emit('click')"
              >
                <span class="font-inter text-base font-bold leading-none">PLAY NOW</span>
              </CoreButton>
            </div>
          </div>
        </div>
      </UCarousel>
    </div>
  </template>
  <template v-else>
    <div class="w-full min-h-[23.75rem] max-w-[81rem] mx-auto">
      <UCarousel
        ref="carousel"
        v-slot="{ item, index }"
        class="carousel"
        :items="gameList"
        align="start"
        loop
        arrows
        :ui="{
          viewport: 'h-full',
          container: `h-full ${slidesGap}`,
          item: 'self-stretch aspect-[611/413] basis-1/2 min-h-[25.8125rem] max-w-[38.1875rem]',
        }"
        :prev="{
          variant: 'solid',
          ui: {
            base: [
              'flex justify-center items-center gap-2.5 shrink-0',
              'border! border-white/10! bg-white/5! rounded-10px! w-[58px]! h-[58px]! p-0 -translate-x-8',
            ],
          },
        }"
        :next="{
          variant: 'solid',
          ui: {
            base: [
              'flex justify-center items-center gap-2.5 shrink-0',
              'border! border-white/10! bg-white/5! rounded-10px! w-[58px]! h-[58px]! p-0 translate-x-8',
            ],
          },
        }"
        prev-icon="i-lucide-chevron-left"
        next-icon="i-lucide-chevron-right"
      >
        <div
          class="flex py-1.5 pr-[2.875rem] pl-1.5 h-full items-center gap-10 rounded-[10px] border border-white/10 bg-white/5"
          @click="$emit('click')"
        >
          <video
            :id="`video-item-${index}`"
            :ref="(el) => (videoElements[index] = el as HTMLVideoElement)"
            loop
            autoplay
            muted
            playsinline
            webkit-playsinline
            width="100%"
            preload="auto"
            :poster="item.imagePoster"
            class="rounded-10px overflow-hidden aspect-[245/380] h-full object-cover"
          >
            <source :src="item.videoSrc" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <NuxtImg :src="item.imageCover" class="hidden opacity-0" />
          <div class="h-full w-full py-0 flex flex-col items-start justify-evenly">
            <NuxtImg
              class="max-w-[90%] max-h-[8.75rem]"
              :src="item.imageLogo"
              densities="x1 x2"
              format="webp"
              :alt="`${item.name} logo`"
            />

            <div class="flex flex-col gap-6">
              <p class="text-left text-white/80 text-xl leading-[135%]">{{ item.description }}</p>

              <CoreButton
                color="green"
                class="w-full h-[3.125rem] px-8 py-4 justify-center items-center"
                @click="$emit('click')"
              >
                <span class="font-inter text-base font-bold leading-none">PLAY NOW</span>
              </CoreButton>
            </div>
          </div>
        </div>
      </UCarousel>
    </div>
  </template>
</template>
